import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Clock, Ban } from 'lucide-react';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';

type ErrorCode =
  | 'invalid'
  | 'expired'
  | 'already-confirmed'
  | 'already-used'
  | 'cancelled'
  | 'in-msr'
  | 'unknown';

type PageState =
  | { status: 'loading' }
  | { status: 'success' }
  | { status: 'error'; code: ErrorCode };

class TokenError extends Error {
  code: ErrorCode;
  constructor(code: ErrorCode) {
    super(code);
    this.code = code;
  }
}

async function declinePriceByToken(token: string): Promise<void> {
  const res = await fetch('/api/orders/decline-price-by-token', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (res.ok) return;

  const body: { error?: string; code?: string } = await res
    .json()
    .catch(() => ({}));

  if (res.status === 404) throw new TokenError('invalid');
  if (res.status === 410) throw new TokenError('expired');
  if (res.status === 409 && body.code === 'cancelled')
    throw new TokenError('cancelled');
  if (res.status === 409 && body.code === 'already-used')
    throw new TokenError('already-used');
  if (res.status === 409 && body.code === 'in-msr')
    throw new TokenError('in-msr');
  if (res.status === 409) throw new TokenError('already-confirmed');
  throw new TokenError('unknown');
}

const ERROR_CONFIG: Record<
  ErrorCode,
  {
    icon: React.ReactNode;
    titleKey: string;
    messageKey: string;
    ctaKey?: string;
    ctaHref?: string;
  }
> = {
  invalid: {
    icon: <XCircle className="text-error-500" size={48} />,
    titleKey: 'decline_price.error.invalid.title',
    messageKey: 'decline_price.error.invalid.message',
  },
  expired: {
    icon: <Clock className="text-warning-700" size={48} />,
    titleKey: 'decline_price.error.expired.title',
    messageKey: 'decline_price.error.expired.message',
    ctaKey: 'decline_price.error.expired.cta',
    ctaHref: '/',
  },
  'already-confirmed': {
    icon: <CheckCircle className="text-prim" size={48} />,
    titleKey: 'decline_price.error.already_confirmed.title',
    messageKey: 'decline_price.error.already_confirmed.message',
  },
  'already-used': {
    icon: <CheckCircle className="text-prim" size={48} />,
    titleKey: 'decline_price.error.already_used.title',
    messageKey: 'decline_price.error.already_used.message',
  },
  cancelled: {
    icon: <Ban className="text-neut-subtle" size={48} />,
    titleKey: 'decline_price.error.cancelled.title',
    messageKey: 'decline_price.error.cancelled.message',
  },
  'in-msr': {
    icon: <Ban className="text-warning-700" size={48} />,
    titleKey: 'decline_price.error.in_msr.title',
    messageKey: 'decline_price.error.in_msr.message',
  },
  unknown: {
    icon: <XCircle className="text-error-500" size={48} />,
    titleKey: 'decline_price.error.unknown.title',
    messageKey: 'decline_price.error.unknown.message',
    ctaKey: 'decline_price.error.unknown.cta',
    ctaHref: '/',
  },
};

export function DeclinePrice() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('decline-price');
  const token = searchParams.get('token');
  const [state, setState] = useState<PageState>(
    token ? { status: 'loading' } : { status: 'error', code: 'invalid' }
  );
  const requestedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    // StrictMode double-invokes effects in dev; the second mount must not re-POST
    // because the backend marks the token used on the first call (HTTP 409 on retry).
    if (requestedRef.current) return;
    requestedRef.current = true;

    declinePriceByToken(token)
      .then(() => setState({ status: 'success' }))
      .catch((err: unknown) => {
        const code: ErrorCode =
          err instanceof TokenError ? err.code : 'unknown';
        setState({ status: 'error', code });
      });
  }, [token]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neut-bg-alt">
        <PageLoadingSpinner name={t('decline_price.loading')} />
      </div>
    );
  }

  if (state.status === 'error') {
    return <ErrorView code={state.code} />;
  }

  return <SuccessView />;
}

function SuccessView() {
  const { t } = useTranslation('decline-price');

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-neut-bg-alt p-4"
      data-testid="decline-price-success"
    >
      <div className="bg-neut-bg-alt rounded-xl shadow-md border border-neut-border max-w-md w-full p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <CheckCircle className="text-success-500" size={48} />
          <h1 className="text-xl font-bold text-neut-prim">
            {t('decline_price.success.title')}
          </h1>
          <p className="text-sm text-neut-sec">
            {t('decline_price.success.message')}
          </p>
        </div>
      </div>
    </div>
  );
}

function ErrorView({ code }: { code: ErrorCode }) {
  const { t } = useTranslation('decline-price');
  const config = ERROR_CONFIG[code];

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-neut-bg-alt p-4"
      data-testid="decline-price-error"
    >
      <div className="bg-neut-bg-alt rounded-xl shadow-md border border-neut-border max-w-md w-full p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          {config.icon}
          <h1 className="text-xl font-bold text-neut-prim">
            {t(config.titleKey)}
          </h1>
          <p className="text-sm text-neut-sec">{t(config.messageKey)}</p>
          {config.ctaKey && config.ctaHref && (
            <Link
              to={config.ctaHref}
              className="mt-2 text-sm font-medium text-prim hover:underline"
              data-testid="error-cta-link"
            >
              {t(config.ctaKey)}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
